import { AudioClip, ClipAsset } from './AudioClip';
import { AudioClipConfig, variantFile } from './AudioClipConfig';
import { AudioResolver } from './AudioResolver';

/**
 * Minimum fetch surface we need. We deliberately do not reuse ResourceLoader because
 * audio fetches need ArrayBuffer (for decodeAudioData) or just a streamable URL; the
 * image-decoding pipeline isn't relevant.
 */
export interface AudioFetcher {
  fetchJson(url: string): Promise<unknown>;
  fetchArrayBuffer(url: string): Promise<ArrayBuffer>;
}

export class FetchAudioFetcher implements AudioFetcher {
  async fetchJson(url: string): Promise<unknown> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Audio config fetch failed (${res.status}): ${url}`);
    return res.json();
  }
  async fetchArrayBuffer(url: string): Promise<ArrayBuffer> {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Audio data fetch failed (${res.status}): ${url}`);
    return res.arrayBuffer();
  }
}

type Entry = {
  refcount: number;
  /** Pending load promise; on success transitions to `clip`, on failure clears so callers can retry. */
  promise: Promise<AudioClip>;
  clip: AudioClip | null;
};

/**
 * Refcounted, single-flight clip cache. Callers `acquire` to pin a clip in memory
 * (and trigger load on first reference), `release` when they no longer need it.
 * Buffers are decoded exactly once. `getCached` is a sync probe used by the
 * one-shot fire path (which can't await).
 */
export class AudioClipLibrary {
  private readonly entries = new Map<string, Entry>();

  constructor(
    private readonly ctx: AudioContext,
    private readonly resolver: AudioResolver,
    private readonly fetcher: AudioFetcher = new FetchAudioFetcher(),
  ) {}

  acquire(clipId: string): Promise<AudioClip> {
    let entry = this.entries.get(clipId);
    if (entry) {
      entry.refcount++;
      return entry.promise;
    }
    const promise = this.load(clipId).catch((err) => {
      // Drop the entry on failure so a retry can reload.
      if (this.entries.get(clipId)?.promise === promise) this.entries.delete(clipId);
      throw err;
    });
    entry = { refcount: 1, promise, clip: null };
    this.entries.set(clipId, entry);
    promise.then(
      (clip) => {
        const stored = this.entries.get(clipId);
        if (stored && stored.promise === promise) stored.clip = clip;
      },
      () => {
        // Rejection is reported through `promise` to the awaiting caller; we
        // just need a handler here so this side-chain doesn't surface as an
        // unhandled rejection.
      },
    );
    return promise;
  }

  release(clipId: string): void {
    const entry = this.entries.get(clipId);
    if (!entry) return;
    entry.refcount--;
    if (entry.refcount <= 0) {
      this.entries.delete(clipId);
      // AudioBuffer has no destructor; GC reclaims it once unreferenced.
    }
  }

  /** Force-reload on next acquire. Used by the editor on clip-changed events. */
  invalidate(clipId: string): void {
    this.entries.delete(clipId);
  }

  getCached(clipId: string): AudioClip | null {
    return this.entries.get(clipId)?.clip ?? null;
  }

  /** For diagnostics + tests. */
  refcount(clipId: string): number {
    return this.entries.get(clipId)?.refcount ?? 0;
  }

  private async load(clipId: string): Promise<AudioClip> {
    const configRaw = await this.fetcher.fetchJson(this.resolver.configUrl(clipId));
    // Intentional structural cast (no validator). The asset-manager catalog
    // writes extra top-level fields (`kinds`, `categories`) into clip.json
    // for classification — those must pass through silently. Don't add a
    // schema validator here without first extending the runtime type to
    // tolerate them.
    const config = configRaw as AudioClipConfig;
    if (!config || typeof config !== 'object' || config.id !== clipId) {
      throw new Error(`Audio clip "${clipId}" config has wrong id (got ${config?.id})`);
    }
    if (!Array.isArray(config.variants) || config.variants.length === 0) {
      throw new Error(`Audio clip "${clipId}" has no variants`);
    }

    // Collect every distinct file referenced by any variant (or the clip
    // default if a variant omits its own override).
    const files = new Set<string>();
    for (const v of config.variants) files.add(variantFile(v));

    let asset: ClipAsset;
    if (config.streaming) {
      const urls = new Map<string, string>();
      for (const f of files) urls.set(f, this.resolver.clipFileUrl(clipId, f));
      asset = { kind: 'streaming', urls };
    } else {
      const buffers = new Map<string, AudioBuffer>();
      // Fetch + decode each unique file in parallel.
      await Promise.all(
        Array.from(files).map(async (f) => {
          const data = await this.fetcher.fetchArrayBuffer(this.resolver.clipFileUrl(clipId, f));
          const buf = await this.ctx.decodeAudioData(data);
          buffers.set(f, buf);
        }),
      );
      asset = { kind: 'decoded', buffers };
    }
    return new AudioClip(config, asset);
  }
}
